/**
 * Auto-adds or removes tier gift lines when cart subtotal crosses thresholds.
 */
(function () {
  const SOURCE = 'gift-threshold-auto-add';
  const GIFT_TIER_PROPERTY = '_gift_tier';
  const GIFT_TIER_AUTO_VALUE = 'auto';

  /** Serialized so concurrent cart-update + drawer paint hooks cannot double-/cart/add */
  let reconcileChain = Promise.resolve();

  /**
   * @param {() => void | Promise<void>} fn
   */
  function enqueueGiftReconcile(fn) {
    reconcileChain = reconcileChain.then(fn).catch((e) => {
      console.warn('[Cart gift tiers] reconcile error:', e);
    });
  }

  /** Line added by this script (not a customer purchase of the same variant). */
  function isAutoGiftLine(item) {
    const props = item?.properties;
    if (!props) return false;
    if (Array.isArray(props)) {
      return props.some(
        (entry) => entry?.name === GIFT_TIER_PROPERTY && entry?.value === GIFT_TIER_AUTO_VALUE
      );
    }
    return props[GIFT_TIER_PROPERTY] === GIFT_TIER_AUTO_VALUE;
  }

  function autoGiftVariantInCart(cartData, variantId) {
    if (!cartData?.items?.length) return false;
    const vid = Number(variantId);
    return cartData.items.some((item) => Number(item.variant_id) === vid && isAutoGiftLine(item));
  }

  /** @returns {number} 1-based Shopify line index for an auto-gift line, or -1 */
  function lineIndexForAutoGiftVariant(cartData, variantId) {
    const vid = Number(variantId);
    const idx = cartData.items?.findIndex(
      (item) => Number(item.variant_id) === vid && isAutoGiftLine(item)
    );
    return idx >= 0 ? idx + 1 : -1;
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function drawerSectionsPayload() {
    const cartDrawer = getCartDrawer();
    if (!cartDrawer) return [];
    return cartDrawer.getSectionsToRender().map((s) => s.id);
  }

  async function cartChangeJson(line, quantity, cartDrawer) {
    const sectionsPayload = drawerSectionsPayload(cartDrawer);
    const payload = {
      line,
      quantity,
      sections_url: window.location.pathname,
    };
    if (sectionsPayload.length) {
      payload.sections = sectionsPayload;
    }
    const body = JSON.stringify(payload);
    const response = await fetch(window.routes.cart_change_url, { ...fetchConfig(), body });
    return JSON.parse(await response.text());
  }

  async function addGiftVariant(variantId) {
    const cartDrawer = getCartDrawer();
    const formData = new FormData();
    formData.append('items[0][id]', String(variantId));
    formData.append('items[0][quantity]', '1');
    formData.append(`items[0][properties][${GIFT_TIER_PROPERTY}]`, GIFT_TIER_AUTO_VALUE);
    const sectionsPayload = drawerSectionsPayload(cartDrawer);
    if (sectionsPayload.length) {
      formData.append('sections', sectionsPayload.join(','));
      formData.append('sections_url', window.location.pathname);
    }
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;
    const response = await fetch(window.routes.cart_add_url, config);
    return response.json();
  }

  function changeFailed(parsed) {
    return Boolean(parsed?.errors ?? parsed?.status);
  }

  /** Variant IDs configured as tier gifts (normalized numbers). */
  function giftTierVariantIds(tiers) {
    return new Set(
      tiers
        .map((t) => Number(t.variantId))
        .filter((id) => id > 0)
    );
  }

  /**
   * Per-line cents after discounts (matches cart drawer totals, not compare-at/original_line_price).
   */
  function itemDiscountedLineCents(item) {
    if (item.final_line_price != null) {
      const n = Number(item.final_line_price);
      if (!Number.isNaN(n)) return n;
    }

    const qty = Number(item.quantity) || 1;

    if (item.discounted_price != null) {
      const unit = Number(item.discounted_price);
      if (!Number.isNaN(unit)) return Math.round(unit * qty);
    }

    if (item.final_price != null) {
      const whole = Number(item.final_price);
      if (!Number.isNaN(whole)) return whole;
    }

    if (item.line_price != null) {
      const line = Number(item.line_price);
      if (!Number.isNaN(line)) return line;
    }

    const unit = Number(item.price);
    if (!Number.isNaN(unit)) return Math.round(unit * qty);

    return 0;
  }

  /**
   * Spend used for thresholds — keeps parity with Liquid progress bar ({ cart.total_price }).
   */
  function qualifyingSpendCents(cartData, tiers) {
    const total = Number(cartData?.total_price ?? 0);
    if (!cartData?.items?.length || !tiers?.length) return total;

    const giftIds = giftTierVariantIds(tiers);
    let giftContribution = 0;
    for (const item of cartData.items) {
      if (!giftIds.has(Number(item.variant_id))) continue;
      if (!isAutoGiftLine(item)) continue;
      giftContribution += itemDiscountedLineCents(item);
    }

    return Math.max(0, total - giftContribution);
  }

  /** @returns {Promise<void>} */
  async function processGiftThresholds(event) {
    if (event?.source === SOURCE) return;

    const tiers = window.cartGiftThresholds;
    if (!tiers?.length) return;

    let cartData = event?.cartData;
    if (!cartData) {
      try {
        const r = await fetch(`${window.routes.cart_url}.js`);
        cartData = await r.json();
      } catch {
        return;
      }
    }

    const cartDrawer = getCartDrawer();

    /** @type {object|null} */
    let lastResponse = null;
    let mutated = false;

    for (const tier of tiers) {
      const threshold = Number(tier.thresholdCents);
      const variantId = tier.variantId;
      if (!threshold || !variantId) continue;
      if (qualifyingSpendCents(cartData, tiers) >= threshold) continue;
      if (!autoGiftVariantInCart(cartData, variantId)) continue;

      const line = lineIndexForAutoGiftVariant(cartData, variantId);
      if (line < 1) continue;

      const parsed = await cartChangeJson(line, 0, cartDrawer);
      if (changeFailed(parsed)) break;
      lastResponse = parsed;
      mutated = true;
      cartData = parsed;
    }

    for (const tier of tiers) {
      const threshold = Number(tier.thresholdCents);
      const variantId = tier.variantId;
      if (!threshold || !variantId) continue;
      if (qualifyingSpendCents(cartData, tiers) < threshold) continue;
      if (autoGiftVariantInCart(cartData, variantId)) continue;

      try {
        lastResponse = await addGiftVariant(variantId);
      } catch (e) {
        console.warn('[Cart gift tiers] Add request failed:', e);
        break;
      }
      if (changeFailed(lastResponse)) {
        console.warn('[Cart gift tiers] Add failed:', lastResponse?.message || lastResponse?.description, lastResponse);
        break;
      }
      mutated = true;
      cartData = lastResponse;
    }

    if (mutated && lastResponse && !changeFailed(lastResponse)) {
      const drawer = getCartDrawer();
      if (drawer && lastResponse.sections) {
        drawer.renderContents(lastResponse);
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: lastResponse });
    }
  }

  function patchCartDrawerAfterPaint() {
    const Drawer = typeof customElements !== 'undefined' && customElements.get('cart-drawer');
    const proto = Drawer?.prototype;
    if (!proto || typeof proto.renderContents !== 'function' || proto.renderContents.__giftTierPatched) return;

    const original = proto.renderContents;
    proto.renderContents = function giftTierRenderWrap(parsedState) {
      original.call(this, parsedState);
      const tiers = window.cartGiftThresholds;
      if (!tiers?.length) return;

      queueMicrotask(() => {
        const hasCart =
          parsedState != null &&
          typeof parsedState === 'object' &&
          parsedState.total_price != null &&
          Array.isArray(parsedState.items);

        enqueueGiftReconcile(() =>
          processGiftThresholds(hasCart ? { cartData: parsedState, source: 'cart-drawer-painted' } : {})
        );
      });
    };
    proto.renderContents.__giftTierPatched = true;
  }

  function start() {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      enqueueGiftReconcile(() => processGiftThresholds(event));
    });
    patchCartDrawerAfterPaint();
    if (window.cartGiftThresholds?.length) {
      enqueueGiftReconcile(() => processGiftThresholds({}));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
